import { useEffect, useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CalendarDays, Info, Loader2, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import type { MeetingKind } from "@/api/client360Meetings";
import {
  scheduleProspectMeeting,
  type ScheduleProspectMeetingPayload,
} from "@/api/prospects";

/**
 * D-134 W4 — зустріч з ЛІДОМ (account-centric, target_holding_id=NULL). Окремий лін-дровер,
 * щоб НЕ чіпати holding-флоу ScheduleMeetingDrawer (existing-клієнти недоторкані). Лід без
 * Contact-сутності → немає контакт-селекта; відповідальний = менеджер (backend default).
 */
const DURATIONS = [30, 60, 90] as const;

function defaultPlannedAt(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ScheduleProspectMeetingDrawer({
  open,
  onClose,
  accountId,
  prospectName,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  accountId: string;
  prospectName: string;
  onCreated?: () => void;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();

  const [kind, setKind] = useState<MeetingKind>("meeting");
  const [plannedAt, setPlannedAt] = useState(defaultPlannedAt);
  const [duration, setDuration] = useState<30 | 60 | 90>(60);
  const [goal, setGoal] = useState("");
  const [expected, setExpected] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: ScheduleProspectMeetingPayload) => scheduleProspectMeeting(accountId, body),
    // D-134 — refresh Meetings Queue list + filter counts from ANY entry point
    // (page «Запланувати» / topbar «+ Створити»), not just via the page callback.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meetingsQueue"] });
      qc.invalidateQueries({ queryKey: ["homeSummary"] });
    },
  });

  useEffect(() => {
    if (open) { setPlannedAt(defaultPlannedAt()); return; }
    setKind("meeting"); setDuration(60); setGoal(""); setExpected(""); setErr(null);
  }, [open]);

  if (!open) return null;

  const canSubmit = plannedAt.trim() !== "" && !mutation.isPending;

  const submit = async () => {
    if (plannedAt.trim() === "") { setErr("Вкажіть дату та час зустрічі"); return; }
    try {
      await mutation.mutateAsync({
        kind,
        plannedAt,
        durationMinutes: duration,
        goal: goal.trim() || undefined,
        expectedResult: expected.trim() || undefined,
      });
      onCreated?.();
      onClose();
    } catch (e) {
      const status = (e as { status?: number } | null)?.status;
      if (status === 403) {
        setErr("Лід поза вашою зоною відповідальності.");
      } else {
        setErr(e instanceof Error ? e.message : "Не вдалося запланувати зустріч");
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full overflow-y-auto bg-background shadow-2xl outline-none sm:w-[440px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
            <CalendarDays className="h-4 w-4 text-primary" aria-hidden /> Зустріч з лідом
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          {/* Лід — badge «Потенційний клієнт» + назва (де зазвичай ім'я клієнта) */}
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2.5 text-sm">
            <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span className="min-w-0 flex-1 truncate font-medium">{prospectName}</span>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold text-primary">
              Потенційний клієнт
            </span>
          </div>

          {/* Тип */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Тип</label>
            <div className="flex gap-2">
              <Seg on={kind === "meeting"} onClick={() => setKind("meeting")}>Зустріч</Seg>
              <Seg on={kind === "online"} onClick={() => setKind("online")}>Онлайн</Seg>
            </div>
          </div>

          {/* Дата / час */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Дата / час <span className="text-destructive">*</span>
            </label>
            <Input
              type="datetime-local"
              value={plannedAt}
              onChange={(e) => setPlannedAt(e.target.value)}
              className={cn(err && plannedAt.trim() === "" && "border-destructive")}
            />
          </div>

          {/* Тривалість */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Тривалість</label>
            <div className="flex gap-2">
              {DURATIONS.map((d) => (
                <Seg key={d} on={duration === d} onClick={() => setDuration(d)}>{d} хв</Seg>
              ))}
            </div>
          </div>

          {/* Ціль */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Ціль зустрічі</label>
            <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="напр. знайомство, презентація" />
          </div>

          {/* Очікуваний результат */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Очікуваний результат</label>
            <Input value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="напр. інтерес до співпраці" />
          </div>

          <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-[11.5px] text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>Лід залишиться в CRM; коли створення клієнта буде готове — оформите як реального. Звіт обов'язковий після проведення.</span>
          </div>

          {err && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {err}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 flex gap-2 border-t bg-background px-5 py-4">
          <Button type="button" onClick={submit} disabled={!canSubmit} className="h-11 flex-1 text-sm font-semibold">
            {mutation.isPending ? (<><Loader2 className="h-4 w-4 animate-spin" /> Планування…</>) : "Запланувати"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose} className="h-11 text-sm">Скасувати</Button>
        </div>
      </div>
    </div>
  );
}

function Seg({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
        on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
